import React, { useEffect, useState, useRef, useCallback } from "react";
import "../styles/AdminMagazine.css";
import { API_BASE, MAGAZINE_API, getImageUrl } from "../config/api";
import { showConfirm } from "../utils/customDialog";

const initialForm = {
  title: "",
  category: "",
  month: "",
  year: "",
  description: "",
  coverImage: null,
  file: null,
};

function AdminMagazine() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [activeTab, setActiveTab] = useState("all-magazines");
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const popupRef = useRef(null);

  // ================= POPUP =================
  const showPopup = useCallback((msg, type = "success") => {
    if (popupRef.current && document.body.contains(popupRef.current)) {
      popupRef.current.remove();
    }

    const div = document.createElement("div");
    popupRef.current = div;

    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === "error" ? "#dc2626" : "#5d1d2b"};
        color: #fff;
        padding: 14px 18px;
        border-radius: 12px;
        z-index: 99999;
        box-shadow: 0 10px 25px rgba(93, 29, 43, 0.25);
        font-size: 14px;
        max-width: 320px;
        font-weight: 600;
        animation: slideIn 0.3s ease;
      ">
        ${msg}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
    `;

    document.body.appendChild(div);

    setTimeout(() => {
      if (div && document.body.contains(div)) {
        div.remove();
      }

      popupRef.current = null;
    }, 2500);
  }, []);

  /* ================= FETCH PUBLICATIONS ================= */
  const fetchPublications = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${MAGAZINE_API}?action=list`);
      const data = await res.json();

      setPublications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch error:", err);
      setPublications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublications();
  }, []);



  /* ================= HANDLE INPUT ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };



  /* ================= HANDLE COVER IMAGE ================= */
  const handleCoverImage = (e) => {
    const image = e.target.files?.[0];

    if (image) {
      setPreview(URL.createObjectURL(image));
    }

    setForm((prev) => ({
      ...prev,
      coverImage: image,
    }));
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    if (!form.title.trim()) {
      showPopup("Title is required", "error");
      return false;
    }

    if (!editingId && !form.coverImage) {
      showPopup("Cover image is required", "error");
      return false;
    }

    return true;
  };

  /* ================= RESET ================= */
  const resetForm = () => {
    setForm(initialForm);
    setPreview(null);
    setEditingId(null);
  };

  /* ================= START EDIT ================= */
  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.content || item.description || "",
      coverImage: null,
    });
    if (item.cover_image) {
      setPreview(getImageUrl(item.cover_image, `${API_BASE}${item.cover_image}`));
    } else {
      setPreview(null);
    }
    setActiveTab("upload-magazine");
  };

  /* ================= ADD OR UPDATE PUBLICATION ================= */
  const handleAdd = async () => {
    if (!validateForm()) return;

    try {
      setUploading(true);

      const formData = new FormData();

      if (editingId) {
        formData.append("action", "update");
        formData.append("id", editingId);
      } else {
        formData.append("action", "add");
      }

      formData.append("title", form.title);
      formData.append("type", "pdf");
      formData.append("description", form.description);

      if (form.coverImage) {
        formData.append("cover_image", form.coverImage);
      }

      const res = await fetch(`${MAGAZINE_API}?action=${editingId ? "update" : "add"}`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.status === "success") {
        showPopup(editingId ? "Magazine updated successfully" : "Magazine uploaded successfully");

        resetForm();
        fetchPublications();
        setActiveTab("all-magazines");
      } else {
        showPopup(data.message || "Failed", "error");
      }
    } catch (err) {
      console.error("Upload/Update error:", err);
      showPopup("Server error", "error");
    } finally {
      setUploading(false);
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    const ok = await showConfirm({
      title: "Delete Magazine",
      message: "Are you sure you want to delete this publication?",
      subMessage: "This will remove the magazine entry and its PDF/cover files.",
      confirmText: "Delete Magazine",
      type: "danger"
    });

    if (!ok) return;

    try {
      const res = await fetch(`${MAGAZINE_API}?action=delete&id=${id}`);
      const data = await res.json();

      if (data.status === "success") {
        setPublications((prev) =>
          prev.filter((item) => item.id !== id)
        );

        showPopup("Publication deleted successfully");
      } else {
        showPopup(data.message || "Delete failed", "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showPopup("Server error", "error");
    }
  };

  const filteredPublications = publications.filter((pub) =>
    pub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (pub.content || pub.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="admin-container magazine-admin-container">

      {/* Header with Dashboard Metrics */}
      <div className="publications-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-journal-bookmark-fill"></i> Spiritual Magazine Manager
          </h1>
          <p>Publish and manage spiritual digital magazine cover issues for subscribers.</p>
        </div>

        <div className="publications-metrics-grid">
          <div className="metric-card" onClick={() => setActiveTab("all-magazines")} style={{ cursor: "pointer" }}>
            <div className="metric-icon magazine"><i className="bi bi-book"></i></div>
            <div className="metric-info">
              <span className="metric-value">{publications.length}</span>
              <span className="metric-label">Total Issues</span>
            </div>
          </div>
          {publications.length > 0 && (
            <div className="metric-card" style={{ maxWidth: "280px" }}>
              <div className="metric-icon categories"><i className="bi bi-clock-history"></i></div>
              <div className="metric-info">
                <span className="metric-value" style={{ fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "160px", display: "block" }}>
                  {publications[0].title}
                </span>
                <span className="metric-label">Latest Issue</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PAGE TABS NAVIGATION */}
      <div className="publications-tabs">
        <button
          type="button"
          className={`pub-tab-btn ${activeTab === "all-magazines" ? "active" : ""}`}
          onClick={() => setActiveTab("all-magazines")}
        >
          <i className="bi bi-grid-3x3-gap"></i>
          <span className="tab-text-container">
            <strong>Magazine Catalog</strong>
            <span className="tab-subtext">View and manage issues</span>
          </span>
          <span className="badge-count">{publications.length}</span>
        </button>

        <button
          type="button"
          className={`pub-tab-btn ${activeTab === "upload-magazine" ? "active" : ""}`}
          onClick={() => {
            if (!editingId) resetForm();
            setActiveTab("upload-magazine");
          }}
        >
          <i className={editingId ? "bi bi-pencil-square" : "bi bi-plus-circle"}></i>
          <span className="tab-text-container">
            <strong>{editingId ? "Edit Issue" : "Publish Issue"}</strong>
            <span className="tab-subtext">{editingId ? "Update existing magazine issue" : "Publish a new cover issue"}</span>
          </span>
        </button>
      </div>

      {/* ================= FORM SECTION ================= */}
      {activeTab === "upload-magazine" && (
        <div className="tab-pane active-fade">
          <div className="admin-card book-form-card-v2" style={{ maxWidth: "800px", margin: "0 auto" }}>
            <h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "10px", color: "#5d1d2b" }}>
              <i className={editingId ? "bi bi-pencil-square" : "bi bi-plus-circle"}></i>
              {editingId ? "Edit Magazine Issue" : "Publish New Issue"}
            </h2>
            <p className="card-subtitle-desc" style={{ fontSize: "14px", color: "#64748b", marginTop: "-20px", marginBottom: "30px" }}>
              {editingId ? "Modify details of the selected magazine issue cover below." : "Enter details to publish a new digital cover issue in the directory."}
            </p>

            {/* Panel 1: Magazine Details */}
            <div className="form-panel-card" style={{ marginBottom: "25px" }}>
              <h3 className="panel-title">
                <i className="bi bi-file-earmark-text"></i> Magazine Details
              </h3>
              
              <div className="form-group-v2">
                <label>Magazine Title *</label>
                <div className="magazine-input-with-icon">
                  <i className="bi bi-journal-text"></i>
                  <input
                    type="text"
                    name="title"
                    placeholder="Enter magazine title"
                    value={form.title}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group-v2">
                <label>Description / Snippet</label>
                <textarea
                  name="description"
                  placeholder="Enter a brief description or preview snippet for subscribers..."
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            </div>

            {/* Panel 2: Cover Media */}
            <div className="form-panel-card" style={{ marginBottom: "25px" }}>
              <h3 className="panel-title">
                <i className="bi bi-image"></i> Cover Media
              </h3>
              
              <div className="upload-box">
                <i className="bi bi-cloud-arrow-up" style={{ fontSize: "36px", color: "#bc9341", marginBottom: "8px", display: "block" }}></i>
                <label style={{ fontSize: "14px", fontWeight: "700", color: "#5d1d2b", cursor: "pointer", display: "block", marginBottom: "6px" }}>
                  {editingId ? "Change Cover Image" : "Upload Cover Image *"}
                </label>
                <span style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "12px" }}>Supports PNG, JPG, JPEG, WEBP files</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImage}
                  style={{ margin: "0 auto", display: "block" }}
                />
              </div>

              {preview && (
                <div className="image-preview" style={{ marginTop: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Preview:</span>
                  <img src={preview} alt="Preview" style={{ maxWidth: "160px", height: "220px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="form-actions-row-v2">
              <button
                type="button"
                className="btn-cancel-action-v2"
                onClick={resetForm}
              >
                <i className="bi bi-x-circle"></i> {editingId ? "Cancel Edit" : "Reset Form"}
              </button>
              <button
                className="btn-submit-action-v2"
                onClick={handleAdd}
                disabled={uploading}
              >
                <i className={editingId ? "bi bi-check-circle-fill" : "bi bi-plus-circle-fill"}></i>
                {uploading ? "Processing..." : (editingId ? "Save Details" : "Publish Issue")}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ================= LIST SECTION ================= */}
      {activeTab === "all-magazines" && (
        <div className="tab-pane active-fade">
          <div className="catalog-toolbar" style={{ display: "flex", gap: "16px", marginBottom: "30px", flexWrap: "wrap", alignItems: "center" }}>
            <div className="search-bar-wrapper" style={{ position: "relative", flex: "1 1 320px", maxWidth: "450px" }}>
              <i className="bi bi-search" style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "16px", pointerEvents: "none" }}></i>
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "100%", padding: "12px 45px 12px 48px", paddingLeft: "48px", borderRadius: "14px", border: "none", backgroundColor: "#f1f5f9", fontSize: "14px", color: "#1e293b", transition: "all 0.3s ease" }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={() => setSearchQuery("")} style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#cbd5e1", fontSize: "16px", cursor: "pointer", padding: "0", display: "inline-flex", alignItems: "center" }}>
                  <i className="bi bi-x-circle-fill"></i>
                </button>
              )}
            </div>
          </div>

          <div className="publications-catalog-section" style={{ width: "100%" }}>
            {loading && (
              <div className="category-loading" style={{ textAlign: "center", padding: "50px" }}>
                <div className="spinner-border text-primary" role="status" style={{ color: "#5d1d2b", width: "3rem", height: "3rem" }}></div>
                <p style={{ marginTop: "15px", fontWeight: "600", color: "#64748b" }}>Loading publications...</p>
              </div>
            )}

            {!loading && filteredPublications.length === 0 && (
              <div className="empty-catalog-state" style={{ textAlign: "center", padding: "50px", background: "#ffffff", borderRadius: "16px", border: "2px dashed #cbd5e1" }}>
                <i className="bi bi-journal-x" style={{ fontSize: "48px", color: "#94a3b8", marginBottom: "15px", display: "block" }}></i>
                <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px 0" }}>No magazines found</h3>
                <p style={{ color: "#64748b", margin: 0 }}>Publish your first magazine issue cover using the Publish tab above.</p>
              </div>
            )}

            {!loading && filteredPublications.length > 0 && (
              <div className="admin-grid magazine-grid">
                {filteredPublications.map((item) => (
                  <div key={item.id} className="admin-card magazine-card book-card">
                    
                    {/* Image Container */}
                    <div className="magazine-card-image-container book-card-image-container">
                      {item.cover_image ? (
                        <img
                          src={getImageUrl(item.cover_image, `${API_BASE}${item.cover_image}`)}
                          alt={item.title}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#94a3b8" }}>
                          <i className="bi bi-book" style={{ fontSize: "48px" }}></i>
                          <span style={{ display: "block", fontSize: "12px", marginTop: "8px" }}>No Cover Image</span>
                        </div>
                      )}
                    </div>

                    {/* Body Content */}
                    <div className="magazine-card-body book-card-body">
                      <div>
                        <h3>{item.title}</h3>
                        
                        {(item.content || item.description) && (
                          <p className="desc">
                            {item.content || item.description}
                          </p>
                        )}
                      </div>

                      {/* Actions Row */}
                      <div className="magazine-card-actions book-card-actions">
                        <button
                          type="button"
                          className="mag-edit-btn edit-btn"
                          onClick={() => handleEdit(item)}
                        >
                          <i className="bi bi-pencil-square"></i> Edit
                        </button>
                        <button
                          type="button"
                          className="mag-delete-btn delete-btn"
                          onClick={() => handleDelete(item.id)}
                        >
                          <i className="bi bi-trash3"></i> Delete
                        </button>
                      </div>

                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMagazine;
